import { FC, Fragment, useReducer } from 'react';

import { Plus } from 'lucide-react';
import { omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';

import { EntryRewardFormDialog } from '../entryRewardFormDialog/EntryRewardFormDialog';
import { IEntryRewardForm, initialValues } from '../form';

import { entryRewardService } from 'v2/api/entryRewardService';
import { rewardsService } from 'v2/api/rewardsService';
import { handleHttpError } from 'v2/utils';

interface CreateModalFormProps {
  onSuccess: VoidFunction;
}

export const CreateEntryRewardButton: FC<CreateModalFormProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerCreateEntryReward, isMutating: isEntryRewardMutating } = useSWRMutation(
    'createEntryReward',
    entryRewardService.createEntryReward,
    {
      onSuccess: () => {
        onSuccess();
        toast.success('The entry reward has been successfully created');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerCreateReward, isMutating: isRewardMutating } = useSWRMutation(
    'createReward',
    rewardsService.createReward,
    { onError: handleHttpError }
  );

  const handleSubmit = (values: IEntryRewardForm, resetForm: VoidFunction) => {
    triggerCreateReward({ type: values.type, value: values.value })
      .then((r) => {
        return triggerCreateEntryReward({
          ...omit(values, ['type', 'value']),
          activationDate: values.activationDate.format(),
          expirationDate: values.expirationDate?.format() ?? '',
          rewardId: r.id,
        });
      })
      .then(() => {
        resetForm();
      });
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Plus />} variant="contained" size="large">
        Add Entry Reward
      </Button>

      <EntryRewardFormDialog
        title="Create Entry Reward"
        isOpen={isOpen}
        onClose={setOpen}
        initialValues={initialValues}
        isSubmitting={isEntryRewardMutating || isRewardMutating}
        onSubmit={handleSubmit}
      />
    </Fragment>
  );
};
